import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiLoader } from "react-icons/fi";

const MAX_DROPDOWN_HEIGHT = 150;
const SEARCH_INPUT_HEIGHT = 55;

interface Option {
  value: string;
  label: string;
}

interface CustomMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  isSearchable?: boolean;
}

export const CustomMultiSelect = ({
  options,
  value,
  onChange,
  onSearch,
  onLoadMore,
  isLoading = false,
  placeholder = "Select options...",
  className = "",
  isSearchable = true,
}: CustomMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;

      let dropdownHeight = MAX_DROPDOWN_HEIGHT;
      if (isSearchable) {
        dropdownHeight += SEARCH_INPUT_HEIGHT;
      }

      const shouldOpenUpward = spaceBelow < dropdownHeight;

      setDropdownStyle({
        top: shouldOpenUpward ? rect.top - dropdownHeight : rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when opening dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch?.(query);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Select Header */}
      <div
        className="flex items-center justify-between p-2 border rounded-md cursor-pointer bg-white hover:border-gray-400"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1 overflow-hidden">
          {value.length > 0 ? (
            <span
              className="text-sm"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {options
                .filter((opt) => value.includes(opt.value))
                .map((opt) => opt.label)
                .join(", ")}
            </span>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
        <FiChevronDown
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="fixed z-[9999] bg-white border rounded-md shadow-lg"
          style={{
            top: dropdownStyle.top,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
          }}
        >
          {/* Search Input */}
          {isSearchable && (
            <div className="p-2 border-b">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full p-2 text-sm border rounded-md focus:outline-none focus:border-gray-400"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          )}

          {/* Options List */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: MAX_DROPDOWN_HEIGHT }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <FiLoader className="animate-spin" />
              </div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No options found.
              </div>
            ) : (
              <>
                {options.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleToggleOption(option.value)}
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      className="mr-3"
                      readOnly
                    />
                    <span className="text-sm">{option.label}</span>
                  </div>
                ))}
                {onLoadMore &&
                  options.length >= 10 &&
                  options.length % 10 === 0 && (
                    <button
                      type="button"
                      className="w-full p-2 text-sm text-center border-t hover:bg-gray-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoadMore();
                      }}
                    >
                      See more
                    </button>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomMultiSelect;
